import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { connectorTypesService } from '../services/connectorTypesService';
import { connectorTypeSettingsService } from '../services/connectorTypeSettingsService';
import './ConnectorTypeForm.css';

const ConnectorTypeForm = ({ mode = 'create' }) => {
  const [formData, setFormData] = useState({
    name: ''
  });
  const [settings, setSettings] = useState([]);
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [initialLoading, setInitialLoading] = useState(mode !== 'create');
  const navigate = useNavigate();
  const { id } = useParams();

  const isViewMode = mode === 'view';
  const isEditMode = mode === 'edit';
  const isCreateMode = mode === 'create';

  useEffect(() => {
    if (id && (isViewMode || isEditMode)) {
      loadConnectorType();
      if (isViewMode) {
        loadSettings();
      }
    }
  }, [id]);

  const loadConnectorType = async () => {
    try {
      setInitialLoading(true);
      const response = await connectorTypesService.get(id);
      setFormData({
        name: response.connectorType.name
      });
    } catch (err) {
      console.error('Error loading connector type:', err);
      setError(err.message);
    } finally {
      setInitialLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await connectorTypeSettingsService.list({ v2_connector_type_id: id });
      setSettings(response.connectorTypeSettings || []);
    } catch (err) {
      console.error('Error loading settings:', err);
    } finally {
      setLoadingSettings(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (isCreateMode) {
        await connectorTypesService.create(formData);
      } else if (isEditMode) {
        await connectorTypesService.update(id, formData);
      }

      navigate('/admin/connector-types');
    } catch (err) {
      console.error('Error saving connector type:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/connector-types');
  };

  if (initialLoading) {
    return (
      <div className="connector-type-form-page">
        <div className="loading">Loading connector type...</div>
      </div>
    );
  }

  return (
    <div className="connector-type-form-page">
      <div className="form-header">
        <h1>
          {isCreateMode && 'Create Connector Type'}
          {isEditMode && 'Edit Connector Type'}
          {isViewMode && 'View Connector Type'}
        </h1>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              disabled={isViewMode || loading}
              required
              placeholder="Enter connector type name"
            />
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
              </button>
            )}
            {isViewMode && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate(`/admin/connector-types/${id}/edit`)}
              >
                Edit
              </button>
            )}
          </div>
        </form>
      </div>

      {isViewMode && (
        <div className="settings-section">
          <div className="settings-header">
            <h2>Settings</h2>
            <button
              className="btn btn-primary"
              onClick={() => navigate(`/admin/connector-types/${id}/settings`)}
            >
              Manage Settings
            </button>
          </div>

          {loadingSettings ? (
            <div className="loading">Loading settings...</div>
          ) : settings.length === 0 ? (
            <div className="no-settings">
              <p>No settings configured for this connector type.</p>
              <button
                className="btn btn-secondary"
                onClick={() => navigate(`/admin/connector-types/${id}/settings/create`)}
              >
                Create First Setting
              </button>
            </div>
          ) : (
            <div className="settings-list">
              <table className="settings-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map(setting => (
                    <tr key={setting.id}>
                      <td>{setting.name}</td>
                      <td>{new Date(setting.insertdate).toLocaleString()}</td>
                      <td>{new Date(setting.updatedate).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="settings-summary">
                Total: {settings.length} setting{settings.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConnectorTypeForm;
